import { useState, useRef, useCallback } from 'react';
import { Download, Loader2, Check, AlertCircle } from 'lucide-react';
import html2canvas from 'html2canvas';
import { usePdfExport } from '../../hooks/usePdfExport';
import { PdfRenderer } from './PdfRenderer';
import type { ChatMessage, VisualizationData } from '../../types';

interface ExportButtonProps {
  messages: ChatMessage[];
  conversationTitle?: string;
  disabled?: boolean;
}

export function ExportButton({
  messages,
  conversationTitle = 'Nebula Conversation',
  disabled = false,
}: ExportButtonProps) {
  const [currentViz, setCurrentViz] = useState<VisualizationData | null>(null);
  const renderContainerRef = useRef<HTMLDivElement>(null);

  const { exportPdf, progress, isExporting, error, setRenderContainer } = usePdfExport();

  // Set up render container ref
  const handleRenderRef = useCallback(
    (el: HTMLDivElement | null) => {
      renderContainerRef.current = el;
      setRenderContainer(el);
    },
    [setRenderContainer]
  );

  const handleExport = async () => {
    // Collect chart-type visualizations (tables/summary cards are rendered directly by jsPDF)
    const chartVizs: VisualizationData[] = [];
    for (const message of messages) {
      if (message.contentBlocks) {
        for (const block of message.contentBlocks) {
          if (
            block.type === 'visualization' &&
            block.data.config.type !== 'table' &&
            block.data.config.type !== 'summary_card'
          ) {
            chartVizs.push(block.data);
          }
        }
      }
    }

    // Pre-render and capture each chart individually as a PNG
    const chartImages: string[] = [];
    for (const viz of chartVizs) {
      setCurrentViz(viz);
      // Wait for React to re-render PdfRenderer with this visualization
      await new Promise((resolve) => setTimeout(resolve, 600));

      // Capture the rendered chart from the off-screen container
      const chartElement = renderContainerRef.current?.querySelector(
        '[data-pdf-chart]'
      ) as HTMLElement | null;
      if (chartElement) {
        try {
          const canvas = await html2canvas(chartElement, {
            allowTaint: true,
            useCORS: true,
            scale: 2,
            backgroundColor: '#ffffff',
            logging: false,
          } as Parameters<typeof html2canvas>[1]);
          chartImages.push(canvas.toDataURL('image/png'));
        } catch (err) {
          console.error('Failed to capture chart:', err);
        }
      }
    }

    await exportPdf(messages, conversationTitle, {}, chartImages);
    setCurrentViz(null);
  };

  const isDisabled = disabled || messages.length === 0 || isExporting;

  // Progress indicator
  const getProgressIcon = () => {
    if (!progress) return <Download className="h-4 w-4" />;

    switch (progress.status) {
      case 'preparing':
      case 'rendering':
      case 'generating':
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case 'complete':
        return <Check className="h-4 w-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Download className="h-4 w-4" />;
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => !isDisabled && handleExport()}
        disabled={isDisabled}
        className={`
          flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors
          ${
            isDisabled
              ? 'cursor-not-allowed border-border bg-muted/50 text-muted-foreground'
              : 'border-border bg-background text-foreground hover:bg-muted'
          }
        `}
        title={
          messages.length === 0
            ? 'No messages to export'
            : isExporting
            ? 'Exporting...'
            : 'Export conversation as PDF'
        }
      >
        {getProgressIcon()}
        <span className="hidden sm:inline">
          {isExporting
            ? progress?.message || 'Exporting...'
            : 'Export as PDF'}
        </span>
      </button>

      {/* Progress bar */}
      {isExporting && progress && (
        <div className="absolute -bottom-1 left-0 right-0 h-1 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{
              width: `${(progress.current / progress.total) * 100}%`,
            }}
          />
        </div>
      )}

      {/* Error toast */}
      {error && (
        <div className="absolute right-0 top-full z-50 mt-2 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Hidden render container for chart capture */}
      <PdfRenderer ref={handleRenderRef} visualization={currentViz} />
    </div>
  );
}
